/**
 * A job's quantity, as the web and the phone read and correct it
 * (/v1/billing/job-quantity). The vendor states what was done; every level the
 * workflow puts after him may correct it, with a reason. `submitted*` is what
 * was first stated, `current*` what the level in charge says now.
 */
import { brandingBasisForUom } from './branding-line';

export interface JobQuantityVerification {
  readonly verificationId: string;
  readonly assignmentId: string;
  readonly jobRequestId: string;
  readonly workOrderId: string;
  readonly workOrderLineId: string;
  readonly status: string;

  readonly submittedCount: number;
  readonly submittedCountUom: string;
  readonly submittedMeasure: number | null;
  readonly submittedDim1: number | null;
  readonly submittedDim2: number | null;
  readonly submittedDim3: number | null;
  readonly submittedBillable: number;
  readonly submittedUom: string;

  readonly currentCount: number;
  readonly currentCountUom: string;
  readonly currentMeasure: number | null;
  readonly currentDim1: number | null;
  readonly currentDim2: number | null;
  readonly currentDim3: number | null;
  readonly currentBillable: number;
  readonly currentUom: string;

  /** Set only once the workflow ends; after that the figure cannot move. */
  readonly finalBillable: number | null;
  readonly finalUom: string | null;
  readonly frozenAt: string | null;
}

/** One change to a job's quantity, with who made it and at which level. */
export interface JobQuantityRevision {
  readonly revisionId: string;
  readonly actionCode: 'SUBMITTED' | 'REVISED' | 'APPROVED' | 'REJECTED';
  readonly actedAt: string;
  readonly actorUserId: string;
  readonly actorName: string | null;
  readonly actorCode: string | null;
  readonly stepLabel: string | null;
  readonly billableBefore: number | null;
  readonly billableAfter: number | null;
  readonly billableUom: string | null;
  readonly remarks: string | null;
}

/** The quantity with its full trail, and whether this reader may correct it now. */
export interface JobQuantityDetail extends JobQuantityVerification {
  readonly mayRevise: boolean;
  readonly trail: readonly JobQuantityRevision[];
}

export interface SubmitJobQuantityInput {
  readonly assignmentId: string;
  readonly count: number;
  readonly countUom: string;
  readonly dim1?: number;
  readonly dim2?: number;
  readonly dim3?: number;
}

export interface ReviseJobQuantityInput {
  readonly count: number;
  readonly countUom: string;
  readonly dim1?: number;
  readonly dim2?: number;
  readonly dim3?: number;
  readonly reasonCode?: string;
  /** Required whenever the billable figure moves. */
  readonly remarks?: string;
}

/** Whether this reader may state an assignment's quantity now, and in what unit. */
export interface JobQuantityStating {
  readonly mayState: boolean;
  /** The unit the work order is priced in, which the figure is stated in. */
  readonly rateUom: string;
}

export const JOB_QUANTITY_PATH = '/v1/billing/job-quantity';

export const jobQuantityPath = (verificationId: string): string =>
  `${JOB_QUANTITY_PATH}/${encodeURIComponent(verificationId)}`;

export const jobQuantityByAssignmentPath = (assignmentId: string): string =>
  `${JOB_QUANTITY_PATH}?assignmentId=${encodeURIComponent(assignmentId)}`;

export const jobQuantityStatingPath = (assignmentId: string): string =>
  `${JOB_QUANTITY_PATH}/stating?assignmentId=${encodeURIComponent(assignmentId)}`;

/**
 * Whether the figure is measured by height and width. Mirrors billableFrom on
 * the server: a count already in the rate's unit (an area in square feet) is
 * the billable figure itself; only a count of things priced by their size is
 * measured.
 */
export function quantityIsMeasured(row: JobQuantityVerification): boolean {
  return (
    brandingBasisForUom(row.currentUom) === 'AREA'
    && row.currentCountUom.trim().toUpperCase() !== row.currentUom.trim().toUpperCase()
  );
}

/** The billable figure the boxes would give, as the server will compute it. */
export function nextBillable(
  row: JobQuantityVerification,
  count: number,
  dim1: number | null,
  dim2: number | null,
): number {
  return quantityIsMeasured(row) && dim1 && dim2
    ? Math.round(count * dim1 * dim2 * 1000) / 1000
    : count;
}

/** Which of the boxes an earlier level has changed from what was first stated. */
export function quantityFieldChanged(
  row: JobQuantityVerification,
  field: 'count' | 'dim1' | 'dim2',
): boolean {
  if (field === 'count') return row.currentCount !== row.submittedCount;
  if (field === 'dim1') return row.currentDim1 !== row.submittedDim1;
  return row.currentDim2 !== row.submittedDim2;
}
